import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";

import { CHIP_PALETTE, PaletteColor } from "../../shared/chip-colors";
import { BvMenuComponent, BvMenuTriggerForDirective } from "./menu.component";

import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";

const HEX_PATTERN = /^#?([0-9a-fA-F]{6})$/;

/**
 * Color swatch button. Click opens a popover with the curated chip palette
 * + an optional "Custom" hex input. Two-way bound via `value` / `valueChange`.
 *
 *   <bv-color-swatch [(value)]="primary"></bv-color-swatch>
 *   <bv-color-swatch [(value)]="secondary" [allowClear]="true" [labelKey]="'CHIP_SECONDARY_COLOR'"></bv-color-swatch>
 */
@Component({
    selector: "bv-color-swatch",
    templateUrl: "./color-swatch.component.html",
    styleUrls: ["./color-swatch.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "bv-color-swatch-host",
    },
    imports: [BvMenuTriggerForDirective, BvMenuComponent, FormsModule, TranslateModule]
})
export class BvColorSwatchComponent {
  @Input() value: string | null = null;
  @Input() allowClear = false;
  @Input() disabled = false;
  @Input() labelKey = "PICK_A_COLOR";
  @Input() recentColors: string[] = [];

  @Output() valueChange = new EventEmitter<string | null>();

  @ViewChild(BvMenuComponent, { static: true }) menu!: BvMenuComponent;

  showCustom = false;
  customInput = "";
  customError = false;

  readonly palette: PaletteColor[] = CHIP_PALETTE;

  get isPaletteMatch(): boolean {
    if (!this.value) return false;
    return CHIP_PALETTE.some(
      (c) => c.hex.toLowerCase() === (this.value || "").toLowerCase()
    );
  }

  trackByHex(_i: number, c: PaletteColor): string {
    return c.hex;
  }

  trackByString(_i: number, s: string): string {
    return s;
  }

  get filteredRecent(): string[] {
    const paletteSet = new Set(CHIP_PALETTE.map((c) => c.hex.toLowerCase()));
    const cur = (this.value || "").toLowerCase();
    const seen = new Set<string>();
    const result: string[] = [];
    for (const hex of this.recentColors) {
      if (!hex) continue;
      const h = hex.toLowerCase();
      if (paletteSet.has(h) || h === cur || seen.has(h)) continue;
      seen.add(h);
      result.push(hex);
    }
    return result;
  }

  pick(hex: string | null): void {
    this.commit(hex);
    this.menu?.requestClose();
  }

  enterCustom(): void {
    this.showCustom = true;
    this.customError = false;
    this.customInput = this.value || "";
  }

  leaveCustom(): void {
    this.showCustom = false;
    this.customError = false;
  }

  commitCustom(): void {
    const raw = (this.customInput || "").trim();
    const m = raw.match(HEX_PATTERN);
    if (!m) {
      this.customError = true;
      return;
    }
    const normalized = "#" + m[1].toLowerCase();
    this.commit(normalized);
    this.showCustom = false;
    this.menu?.requestClose();
  }

  onNativeColorChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.value) {
      this.customInput = target.value;
      this.commitCustom();
    }
  }

  clear(): void {
    this.commit(null);
    this.menu?.requestClose();
  }

  private commit(value: string | null): void {
    this.value = value;
    this.valueChange.emit(value);
  }
}
